import { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react';
import {
    Box, Button, Flex, Text, VStack, HStack,
    Switch, FormLabel, FormControl, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
    Icon, Popover, PopoverTrigger, PopoverContent, PopoverBody, Input, InputGroup, InputLeftElement, Portal,
} from '@chakra-ui/react';
import type { WidgetConfig, WidgetType, WidgetDomain, WidgetEditorCatalog } from './types';
import { TbCurrencyDollar, TbChartBar, TbChartPie, TbChevronDown, TbSearch, TbCheck, TbTable, TbEye, TbBuildingStore, TbCarOff, TbAlertTriangle } from 'react-icons/tb';
import { DOMAIN_OPTIONS, SUMMARY_METRICS, TIMESERIES_VOLUME_METRICS, TIMESERIES_MARGIN_METRICS, RANKING_METRICS, supportsMarketplaceBreakdown, isStackedMarketplaceMetric } from './types';
import {
    LOCATION_CHART_METRIC_OPTIONS,
    INSURANCE_CHART_METRIC_OPTIONS,
    OEM_CHART_METRIC_OPTIONS,
    VENDOR_CHART_METRIC_OPTIONS,
    CHART_METRIC_OPTIONS,
} from '../components/ChartMetricSelector';
import { INVOICING_DEFAULT_LOCATION_LINE_CUTOFF } from './invoicingCatalog';
import { isReportingComparisonSupported } from '../shared/reportingComparisonUtils';
import { filterChartMetricsForInvoicing, orgShowsInvoicingReportingMetrics } from '../shared/invoicingGatedMetrics';
import AppDrawer from '../../../components/AppDrawer';
import SessionContext from '../../../contexts/SessionContext';

const WIDGET_TYPE_CARDS: { value: WidgetType; label: string; icon: React.ElementType; }[] = [
    { value: 'keyMetric', label: 'Summary Card', icon: TbCurrencyDollar as React.ElementType },
    { value: 'timeSeries', label: 'Bar Chart', icon: TbChartBar as React.ElementType },
    { value: 'pieChart', label: 'Pie Chart', icon: TbChartPie as React.ElementType },
    { value: 'table', label: 'Table', icon: TbTable as React.ElementType },
];

interface VariantGroup {
    label: string;
    vendorLabel?: string;
    variants: { value: string; label: string; }[];
}

const VARIANT_GROUPS: VariantGroup[] = [
    {
        label: 'Order Size', variants: [
            { value: 'avgOrderSize', label: 'Avg.' },
            { value: 'medianOrderSize', label: 'Median' },
        ]
    },
    {
        label: 'Gross Margin', vendorLabel: 'Discount', variants: [
            { value: 'avgGrossMarginPct', label: 'Avg.' },
            { value: 'medianGrossMarginPct', label: 'Median' },
        ]
    },
    // Summary-card keys (overview summary API) — same UX as List Price grouping
    {
        label: 'Gross Margin', variants: [
            { value: 'avgGrossMargin', label: 'Avg.' },
            { value: 'medianGrossMargin', label: 'Median' },
        ]
    },
    {
        label: 'Gross Margin (w/o PM)', vendorLabel: 'Discount (w/o PM)', variants: [
            { value: 'avgGrossMarginPctWoPM', label: 'Avg.' },
            { value: 'medianGrossMarginPctWoPM', label: 'Median' },
        ]
    },
    // Summary-card keys (overview summary API)
    {
        label: 'Gross Margin (w/o PM)', variants: [
            { value: 'avgGrossMarginWoPM', label: 'Avg.' },
            { value: 'medianGrossMarginWoPM', label: 'Median' },
        ]
    },
    {
        label: 'Missed Opportunity', variants: [
            { value: 'missedOpportunity', label: 'Total' },
            { value: 'avgMissedOpportunity', label: 'Avg.' },
            { value: 'medianMissedOpportunity', label: 'Median' },
        ]
    },
    {
        label: 'Part Value', variants: [
            { value: 'avgPartValue', label: 'Avg.' },
            { value: 'medianPartValue', label: 'Median' },
        ]
    },
    {
        label: 'List Price', variants: [
            { value: 'avgListPrice', label: 'Avg.' },
            { value: 'medianListPrice', label: 'Median' },
        ]
    },
    {
        label: 'List Price (w/o PM)', variants: [
            { value: 'avgListPriceWoPM', label: 'Avg.' },
            { value: 'medianListPriceWoPM', label: 'Median' },
        ]
    },
    {
        label: '% OEM', variants: [
            { value: 'pctOemVolume', label: 'Volume' },
            { value: 'pctOemCount', label: 'Count' },
        ]
    },
    {
        label: '% LKQ', variants: [
            { value: 'pctLkqVolume', label: 'Volume' },
            { value: 'pctLkqCount', label: 'Count' },
        ]
    },
    {
        label: '% Aftermarket', variants: [
            { value: 'pctAftermarketVolume', label: 'Volume' },
            { value: 'pctAftermarketCount', label: 'Count' },
        ]
    },
];

function generateId() {
    return `widget-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function getDefaultConfig(): WidgetConfig {
    return {
        id: generateId(),
        title: 'New Widget',
        description: '',
        widgetType: 'timeSeries',
        domain: 'order',
        metric: 'orderVolume',
        stat: 'total',
        format: 'currency',
        gridSpan: 2,
        dataSource: 'timeseries',
        dataKey: 'volume',
        comparison: true,
        showComparisonBars: false,
        excludeEvs: false,
        showAllRows: false,
        sort: 'desc',
    };
}

function normalizeConfig(config: WidgetConfig): WidgetConfig {
    let widgetType = normalizeWidgetType(config.widgetType);
    // Legacy: table + Total Orders silently became location rankings — coerce back to bar chart.
    const coercedFromOrderTable = config.domain === 'order' && widgetType === 'table';
    if (coercedFromOrderTable) {
        widgetType = 'timeSeries';
    }
    return {
        ...config,
        widgetType,
        ...(coercedFromOrderTable ? inferDataSource(widgetType, config.domain) : {}),
        comparison: config.comparison ?? true,
        showComparisonBars: config.showComparisonBars ?? false,
        excludeEvs: config.excludeEvs ?? false,
    };
}

function normalizeWidgetType(widgetType: string): WidgetType {
    if (widgetType === 'topList') return 'timeSeries';
    const knownTypes: WidgetType[] = ['keyMetric', 'timeSeries', 'table', 'pieChart'];
    return knownTypes.includes(widgetType as WidgetType)
        ? (widgetType as WidgetType)
        : 'timeSeries';
}

const RANKING_DATA_KEYS: Record<string, string> = {
    location: 'locations',
    vendor: 'vendors',
    insurance: 'insurance',
    oem: 'oems',
    partType: 'partTypes',
    order: 'locations',
};

function isRankingMixDomain(domain: WidgetDomain): boolean {
    return domain === 'partType' || domain === 'location' || domain === 'vendor'
        || domain === 'insurance' || domain === 'oem';
}

function inferDataSource(wt: WidgetType, domain: WidgetDomain): { dataSource: WidgetConfig['dataSource']; dataKey?: string; } {
    if (wt === 'keyMetric') return { dataSource: 'summary' };
    if (wt === 'timeSeries' && domain === 'order') {
        return { dataSource: 'timeseries', dataKey: 'volume' };
    }
    return { dataSource: 'rankings', dataKey: RANKING_DATA_KEYS[domain] || 'locations' };
}

function inferGridSpan(wt: WidgetType): number {
    if (wt === 'keyMetric') return 1;
    if (wt === 'timeSeries') return 2;
    if (wt === 'table') return 2;
    if (wt === 'pieChart') return 1;
    return 2;
}

function inferStatFromMetric(metric: string): 'total' | 'average' | 'median' {
    if (metric.startsWith('median')) return 'median';
    if (metric.startsWith('avg') || metric.startsWith('pct')) return 'average';
    return 'total';
}

function getRankingMetricsForDomain(domain: WidgetDomain, invoicingEnabled: boolean) {
    const source =
        domain === 'vendor'
            ? VENDOR_CHART_METRIC_OPTIONS
            : domain === 'location'
                ? LOCATION_CHART_METRIC_OPTIONS
                : domain === 'insurance'
                    ? INSURANCE_CHART_METRIC_OPTIONS
                    : domain === 'oem'
                        ? OEM_CHART_METRIC_OPTIONS
                        : CHART_METRIC_OPTIONS;

    return filterChartMetricsForInvoicing(source, invoicingEnabled)
        // ETA-change (days) metrics are surfaced on the reporting Vendor tab only,
        // not as dashboard widgets (WidgetFormat has no 'days' format).
        .filter(option => option.format !== 'days')
        .map(option => ({
            value: option.value,
            label: option.label,
            format: option.format as 'currency' | 'percent' | 'number',
            tooltip: option.tooltip,
            stat: inferStatFromMetric(option.value),
        }));
}

function PopoverSelect({ value, options, onChange, placeholder = 'Select…', searchable = false }: {
    value: string;
    options: Array<{ value: string; label: string; }>;
    onChange: (value: string) => void;
    placeholder?: string;
    searchable?: boolean;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [popW, setPopW] = useState<number>(0);

    useEffect(() => {
        if (isOpen && triggerRef.current) setPopW(triggerRef.current.offsetWidth);
    }, [isOpen]);

    useEffect(() => { if (!isOpen) setSearch(''); }, [isOpen]);

    const filtered = searchable && search
        ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
        : options;

    const selectedLabel = options.find(o => o.value === value)?.label ?? placeholder;

    return (
        <Popover
            placement="bottom-start"
            isOpen={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            matchWidth
        >
            <PopoverTrigger>
                <Button
                    ref={triggerRef}
                    variant="outline"
                    w="100%"
                    h="36px"
                    bg="white"
                    borderColor="gray.300"
                    borderRadius="md"
                    fontWeight="normal"
                    textAlign="left"
                    justifyContent="space-between"
                    px={3}
                    _hover={{ borderColor: 'gray.400' }}
                    rightIcon={<Icon as={TbChevronDown as React.ElementType} boxSize={4} color="gray.500" />}
                >
                    <Text noOfLines={1} fontSize="sm" color={value ? 'gray.800' : 'gray.500'}>{selectedLabel}</Text>
                </Button>
            </PopoverTrigger>
            <Portal>
                <PopoverContent w={popW ? `${popW}px` : '100%'} maxH="280px" overflowY="auto" zIndex="popover" onClick={e => e.stopPropagation()}>
                    <PopoverBody p={2}>
                        <VStack align="stretch" spacing={1}>
                            {searchable && (
                                <InputGroup size="sm" mb={1}>
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={TbSearch as React.ElementType} color="gray.400" boxSize={3} />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search…"
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        bg="white"
                                        borderColor="gray.200"
                                    />
                                </InputGroup>
                            )}
                            {filtered.map(opt => {
                                const active = opt.value === value;
                                return (
                                    <Flex
                                        key={opt.value}
                                        align="center"
                                        px={2}
                                        py={1.5}
                                        borderRadius="md"
                                        cursor="pointer"
                                        bg={active ? '#E6F1FD' : 'transparent'}
                                        _hover={{ bg: active ? '#E6F1FD' : 'gray.50' }}
                                        onClick={() => { onChange(opt.value); setIsOpen(false); }}
                                    >
                                        <Text flex={1} fontSize="sm" color={active ? '#0071E3' : 'gray.700'} fontWeight={active ? 'medium' : 'normal'}>
                                            {opt.label}
                                        </Text>
                                        {active && <Icon as={TbCheck as React.ElementType} boxSize={4} color="#0071E3" />}
                                    </Flex>
                                );
                            })}
                            {filtered.length === 0 && (
                                <Text color="gray.500" textAlign="center" py={2} fontSize="sm">No matches</Text>
                            )}
                        </VStack>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
}

interface WidgetEditDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (config: WidgetConfig) => void;
    initialConfig?: WidgetConfig;
    isCreating?: boolean;
    isGm?: boolean;
    preset?: string;
    /** When provided, drives domains/metrics/widget types/toggles for a non-overview dashboard. */
    catalog?: WidgetEditorCatalog;
}

const WidgetEditDrawer = ({ isOpen, onClose, onSave, initialConfig, isCreating, isGm = false, preset, catalog }: WidgetEditDrawerProps) => {
    const sessionContext = useContext(SessionContext);
    const invoicingEnabled = orgShowsInvoicingReportingMetrics(sessionContext);
    const makeDefault = () => (catalog ? catalog.defaultConfig() : getDefaultConfig());
    const [draft, setDraft] = useState<WidgetConfig>(initialConfig ? normalizeConfig(initialConfig) : makeDefault());

    useEffect(() => {
        if (isOpen) {
            setDraft(initialConfig ? normalizeConfig(initialConfig) : makeDefault());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, initialConfig]);

    const update = <K extends keyof WidgetConfig>(key: K, value: WidgetConfig[K]) => {
        setDraft(prev => ({ ...prev, [key]: value }));
    };

    const availableDomains = useMemo(
        () => {
            if (catalog) return catalog.domainsFor?.(draft) ?? catalog.domains;
            return isGm ? DOMAIN_OPTIONS.filter(option => option.value !== 'location') : DOMAIN_OPTIONS;
        },
        [isGm, catalog, draft]
    );

    const handleWidgetTypeChange = (wt: WidgetType) => {
        const src = inferDataSource(wt, draft.domain);
        const span = inferGridSpan(wt);
        setDraft(prev => {
            const next = { ...prev, widgetType: wt, ...src, gridSpan: span };
            if (isRankingMixDomain(draft.domain)) {
                const isPctOfTotal = prev.metric.startsWith('pctOfTotal');
                if (wt === 'pieChart' && !isPctOfTotal) {
                    next.metric = 'pctOfTotalVolume';
                    next.format = 'percent';
                    next.stat = 'total';
                } else if (wt !== 'pieChart' && isPctOfTotal) {
                    next.metric = 'orderVolume';
                    next.format = 'currency';
                    next.stat = 'total';
                }
            }
            return next;
        });
    };

    const handleCardClick = (cardId: string) => {
        if (catalog) {
            setDraft(prev => ({ ...prev, ...catalog.applyCardId(cardId, prev) }));
            return;
        }
        handleWidgetTypeChange(cardId as WidgetType);
    };

    const handleDomainChange = (domain: WidgetDomain) => {
        if (catalog) {
            const cfg = catalog.applyDomain ? catalog.applyDomain(domain, draft) : {};
            setDraft(prev => ({ ...prev, domain, ...cfg }));
            return;
        }
        // Table / pie + Total Orders isn't a mix — fall back to the org timeseries.
        const widgetType = domain === 'order' && (draft.widgetType === 'table' || draft.widgetType === 'pieChart')
            ? 'timeSeries' as WidgetType
            : draft.widgetType;
        const src = inferDataSource(widgetType, domain);
        setDraft(prev => ({ ...prev, domain, widgetType, ...src }));
    };

    const availableMetrics = useMemo(() => {
        if (catalog) return catalog.metricsFor(draft);
        if (draft.widgetType === 'keyMetric') return SUMMARY_METRICS;
        if (draft.domain === 'partType') {
            const partTypeMetrics = new Set([
                'orderVolume', 'orderCost', 'orderCount', 'avgOrderSize', 'medianOrderSize', 'grossProfit',
                'avgGrossMarginPct', 'medianGrossMarginPct',
                'avgGrossMarginPctWoPM', 'medianGrossMarginPctWoPM',
                'grossMarginLossFromPM',
                'missedOpportunity', 'avgMissedOpportunity', 'medianMissedOpportunity',
                'pctOfTotalVolume', 'pctOfTotalCount', 'pctOfTotalProfit',
            ]);
            return RANKING_METRICS.filter(m => partTypeMetrics.has(m.value));
        }
        if (draft.widgetType === 'timeSeries') {
            if (draft.domain === 'order') {
                return [...TIMESERIES_VOLUME_METRICS, ...TIMESERIES_MARGIN_METRICS];
            }
            return getRankingMetricsForDomain(draft.domain, invoicingEnabled);
        }
        if (draft.widgetType === 'table') return getRankingMetricsForDomain(draft.domain, invoicingEnabled);
        if (draft.widgetType === 'pieChart') return RANKING_METRICS.filter(m => m.value.startsWith('pctOfTotal'));
        return getRankingMetricsForDomain(draft.domain, invoicingEnabled);
    }, [draft, catalog, invoicingEnabled]);

    useEffect(() => {
        if (!availableMetrics.some(m => m.value === draft.metric)) {
            const first = availableMetrics[0];
            if (first) {
                setDraft(prev => ({
                    ...prev,
                    metric: first.value,
                    format: first.format,
                    stat: first.stat,
                }));
            }
        }
    }, [availableMetrics, draft.metric]);

    const applyMetricChange = useCallback((newMetric: string) => {
        const entry = availableMetrics.find(m => m.value === newMetric);
        setDraft(prev => {
            const next = { ...prev, metric: newMetric };
            if (entry) {
                next.format = entry.format;
                next.stat = entry.stat;
            }
            if (prev.widgetType === 'timeSeries' && prev.domain === 'order') {
                const marginMetrics = ['avgGrossMarginPct', 'medianGrossMarginPct', 'partListPricePctOfOE', 'avgGrossMarginPctWoPM', 'medianGrossMarginPctWoPM', 'grossMarginLossFromPM', 'missedOpportunity', 'avgMissedOpportunity', 'medianMissedOpportunity'];
                next.dataKey = marginMetrics.includes(newMetric) ? 'margin' : 'volume';
            }
            if (isRankingMixDomain(prev.domain)) {
                const isPctOfTotal = newMetric.startsWith('pctOfTotal');
                if (isPctOfTotal && prev.widgetType !== 'pieChart') {
                    next.widgetType = 'pieChart';
                    Object.assign(next, inferDataSource('pieChart', prev.domain));
                    next.gridSpan = 1;
                } else if (!isPctOfTotal && prev.widgetType === 'pieChart') {
                    next.widgetType = 'timeSeries';
                    Object.assign(next, inferDataSource('timeSeries', prev.domain));
                    next.gridSpan = 2;
                }
            }
            if (catalog?.applyMetric) {
                Object.assign(next, catalog.applyMetric(newMetric, prev));
            }
            return next;
        });
    }, [availableMetrics, catalog]);

    const { condensedOptions, activeGroup, dropdownValue } = useMemo(() => {
        const allValues = new Set(availableMetrics.map(m => m.value));
        const variantGroups: VariantGroup[] = catalog?.variantGroups ?? VARIANT_GROUPS;

        const validGroups = variantGroups.reduce<{ group: VariantGroup; representative: string; members: Set<string>; }[]>((acc, group) => {
            const available = group.variants.filter(v => allValues.has(v.value));
            if (available.length >= 2) {
                acc.push({
                    group: { ...group, variants: available },
                    representative: available[0].value,
                    members: new Set(available.map(v => v.value)),
                });
            }
            return acc;
        }, []);

        const metricToGroup = new Map<string, typeof validGroups[0]>();
        validGroups.forEach(vg => {
            vg.members.forEach(m => metricToGroup.set(m, vg));
        });

        const options: { value: string; label: string; }[] = [];
        const seenReps = new Set<string>();

        for (const metric of availableMetrics) {
            const vg = metricToGroup.get(metric.value);
            if (vg) {
                if (!seenReps.has(vg.representative)) {
                    seenReps.add(vg.representative);
                    const label = draft.domain === 'vendor' && vg.group.vendorLabel ? vg.group.vendorLabel : vg.group.label;
                    options.push({ value: vg.representative, label });
                }
            } else {
                options.push({ value: metric.value, label: metric.label });
            }
        }

        const currentVg = metricToGroup.get(draft.metric);
        const ddValue = currentVg ? currentVg.representative : draft.metric;
        const aGroup = currentVg ? currentVg.group : null;

        return { condensedOptions: options, activeGroup: aGroup, dropdownValue: ddValue };
    }, [availableMetrics, draft.domain, draft.metric, catalog]);

    const currentMetricEntry = availableMetrics.find(m => m.value === draft.metric);
    const domainLabel = availableDomains.find(d => d.value === draft.domain)?.label || draft.domain;
    const metricLabel = currentMetricEntry?.label || draft.metric;
    const autoTitle = draft.widgetType === 'keyMetric'
        ? metricLabel
        : `${metricLabel} by ${domainLabel}`;
    const autoDescription = currentMetricEntry?.tooltip || '';

    const handleSave = () => {
        let config = normalizeConfig({
            ...draft,
            widgetType: normalizeWidgetType(draft.widgetType),
            title: autoTitle,
            description: autoDescription,
        });
        if (catalog) {
            const { excludeEvs: _omit, ...stripped } = config;
            config = stripped;
        }
        if (isCreating) config.id = generateId();
        onSave(config);
    };

    // Which configuration rows apply to the current draft (used to avoid an empty section).
    const showPriorToggle = draft.widgetType !== 'pieChart' && draft.widgetType !== 'table'
        && isReportingComparisonSupported(preset)
        && (catalog ? (catalog.showPriorFor?.(draft) ?? true) : true);
    const showExcludeEvsToggle = !catalog;
    const showMarketplaceToggle = !catalog && supportsMarketplaceBreakdown(draft.domain, draft.widgetType, draft.metric);
    const showRankingsControls = draft.dataSource === 'rankings' && draft.widgetType !== 'pieChart';
    const showSortControls = showRankingsControls || (catalog?.showSortFor?.(draft) ?? false);
    const showLocationLimitControls = catalog?.showLocationLimitFor?.(draft) ?? false;
    const showRestockItemsOnlyToggle = catalog?.showRestockItemsOnlyFor?.(draft) ?? false;
    const hasAnyConfig = showPriorToggle || showExcludeEvsToggle || showMarketplaceToggle || showRankingsControls || showSortControls || showLocationLimitControls || showRestockItemsOnlyToggle;
    const isEditingSummaryCard = !isCreating && draft.widgetType === 'keyMetric';

    const widgetTypeOptions = (catalog?.widgetTypesFor?.(draft) ?? catalog?.widgetTypes ?? WIDGET_TYPE_CARDS).filter(wt => {
        if (isEditingSummaryCard) {
            return wt.value === 'keyMetric';
        }
        if (!isCreating && wt.value === 'keyMetric') return false;
        if (!catalog && wt.value === 'pieChart' && !isRankingMixDomain(draft.domain)) return false;
        // Table is entity rankings, not a date grid — don't offer it for org totals (would silently become locations).
        if (!catalog && wt.value === 'table' && draft.domain === 'order') return false;
        return true;
    });

    return (
        <AppDrawer
            isOpen={isOpen}
            onClose={onClose}
            size="md"
            title={isCreating ? 'Add Widget' : 'Edit Widget'}
            bodyProps={{ pt: 4 }}
            footer={
                <>
                    <Button size="action" variant="outlineGray" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button size="action" bg="#0071E3" color="white" _hover={{ bg: '#3544a0' }} onClick={handleSave}>
                        Save
                    </Button>
                </>
            }
        >
                    <VStack spacing={6} align="stretch">

                        {/* Auto-generated preview */}
                        <Box bg="gray.50" borderRadius="md" p={3} border="1px solid" borderColor="gray.200">
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700">{autoTitle}</Text>
                            {autoDescription && (
                                <Text fontSize="xs" color="gray.500" mt={1}>{autoDescription}</Text>
                            )}
                        </Box>

                        {/* Widget Type */}
                        <Accordion allowMultiple defaultIndex={[0, 1, 2]}>
                            <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="visible" mb={3}>
                                <AccordionItem border="none">
                                    <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                        <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">Widget Type</Text>
                                        <AccordionIcon />
                                    </AccordionButton>
                                    <AccordionPanel px={4} pb={4} pt={1}>
                                        <SimpleGrid columns={2} spacing={3}>
                                            {widgetTypeOptions.map(wt => {
                                                const isActive = catalog ? catalog.selectedCardId(draft) === wt.value : draft.widgetType === wt.value;
                                                const isLocked = isEditingSummaryCard;
                                                return (
                                                    <Flex
                                                        key={wt.value}
                                                        align="center"
                                                        gap={3}
                                                        px={4}
                                                        py={3}
                                                        borderRadius="lg"
                                                        border="2px solid"
                                                        borderColor={isActive ? '#0071E3' : 'gray.200'}
                                                        bg={isActive ? '#E6F1FD' : 'white'}
                                                        cursor={isLocked ? 'default' : 'pointer'}
                                                        _hover={isLocked ? undefined : { borderColor: '#0071E3' }}
                                                        transition="all 0.15s"
                                                        onClick={isLocked ? undefined : () => handleCardClick(wt.value)}
                                                    >
                                                        <Icon as={wt.icon as React.ElementType} boxSize={5} color={isActive ? '#0071E3' : 'gray.500'} />
                                                        <Text fontSize="sm" fontWeight="medium" color={isActive ? '#0071E3' : 'gray.700'}>
                                                            {wt.label}
                                                        </Text>
                                                    </Flex>
                                                );
                                            })}
                                        </SimpleGrid>
                                    </AccordionPanel>
                                </AccordionItem>
                            </Box>

                            {/* Metric Selection */}
                            <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="visible" position="relative" zIndex={2} mb={3}>
                                <AccordionItem border="none">
                                    <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                        <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">Metric</Text>
                                        <AccordionIcon />
                                    </AccordionButton>
                                    <AccordionPanel px={4} pb={4} pt={1}>
                                        <VStack spacing={4} align="stretch">
                                            {draft.widgetType !== 'keyMetric' && (
                                                <FormControl>
                                                    <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">Domain</FormLabel>
                                                    <PopoverSelect
                                                        value={draft.domain}
                                                        options={availableDomains.map(d => ({ value: d.value, label: d.label }))}
                                                        onChange={v => handleDomainChange(v as WidgetDomain)}
                                                        placeholder="Select domain"
                                                    />
                                                </FormControl>
                                            )}
                                            <FormControl>
                                                <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">Metric</FormLabel>
                                                <PopoverSelect
                                                    value={dropdownValue}
                                                    options={condensedOptions}
                                                    onChange={selectedValue => {
                                                        if (selectedValue === dropdownValue) return;
                                                        applyMetricChange(selectedValue);
                                                    }}
                                                    placeholder="Select metric"
                                                    searchable
                                                />
                                            </FormControl>
                                            {activeGroup && (
                                                <FormControl>
                                                    <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
                                                        {activeGroup.variants[0].label === 'Volume' ? 'Measure by' : 'Aggregation'}
                                                    </FormLabel>
                                                    <HStack spacing={0}>
                                                        {activeGroup.variants.map((v, i) => {
                                                            const isActive = v.value === draft.metric;
                                                            const nextIsActive = i < activeGroup.variants.length - 1 && activeGroup.variants[i + 1].value === draft.metric;
                                                            return (
                                                                <Button
                                                                    key={v.value}
                                                                    size="sm"
                                                                    h="32px"
                                                                    flex={1}
                                                                    borderRadius={0}
                                                                    borderLeftRadius={i === 0 ? 'md' : 0}
                                                                    borderRightRadius={i === activeGroup.variants.length - 1 ? 'md' : 0}
                                                                    border="1px solid"
                                                                    borderColor={isActive ? '#0071E3' : 'gray.200'}
                                                                    borderLeftColor={i > 0 ? (isActive ? '#0071E3' : 'transparent') : undefined}
                                                                    borderRightColor={nextIsActive ? 'transparent' : undefined}
                                                                    ml={i > 0 ? '-1px' : undefined}
                                                                    zIndex={isActive ? 1 : 0}
                                                                    bg={isActive ? '#E6F1FD' : 'white'}
                                                                    color={isActive ? '#0071E3' : 'gray.600'}
                                                                    fontWeight={isActive ? 'semibold' : 'normal'}
                                                                    fontSize="sm"
                                                                    _hover={{ bg: isActive ? '#E6F1FD' : 'gray.50' }}
                                                                    onClick={() => applyMetricChange(v.value)}
                                                                >
                                                                    {v.label}
                                                                </Button>
                                                            );
                                                        })}
                                                    </HStack>
                                                </FormControl>
                                            )}
                                        </VStack>
                                    </AccordionPanel>
                                </AccordionItem>
                            </Box>

                            {/* Configuration */}
                            <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="visible" position="relative" zIndex={1}>
                                <AccordionItem border="none">
                                    <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                        <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">Configuration</Text>
                                        <AccordionIcon />
                                    </AccordionButton>
                                    <AccordionPanel px={4} pb={4} pt={1}>
                                        <VStack spacing={4} align="stretch">

                                            {showPriorToggle && (
                                                <Flex align="center" justify="space-between" cursor="pointer" onClick={() => setDraft(prev => {
                                                    const checked = !(prev.showComparisonBars ?? false);
                                                    return {
                                                        ...prev,
                                                        comparison: prev.comparison ?? true,
                                                        showComparisonBars: checked,
                                                    };
                                                })}>
                                                    <HStack spacing={3}>
                                                        <Icon as={TbEye as React.ElementType} boxSize={5} color="gray.500" />
                                                        <Box>
                                                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                                Show prior period
                                                            </Text>
                                                            <Text fontSize="xs" color="gray.500">
                                                                Render prior-period bars alongside current values
                                                            </Text>
                                                        </Box>
                                                    </HStack>
                                                    <Switch isChecked={draft.showComparisonBars ?? false} pointerEvents="none" colorScheme="blue" size="md" />
                                                </Flex>
                                            )}
                                            {showExcludeEvsToggle && (
                                                <Flex align="center" justify="space-between" cursor="pointer" onClick={() => setDraft(prev => ({
                                                    ...prev,
                                                    excludeEvs: !(prev.excludeEvs ?? false),
                                                }))}>
                                                    <HStack spacing={3}>
                                                        <Icon as={TbCarOff as React.ElementType} boxSize={5} color="gray.500" />
                                                        <Box>
                                                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                                Exclude EVs
                                                            </Text>
                                                            <Text fontSize="xs" color="gray.500">
                                                                Exclude Tesla, Lucid, and Rivian from stats
                                                            </Text>
                                                        </Box>
                                                    </HStack>
                                                    <Switch isChecked={draft.excludeEvs ?? false} pointerEvents="none" colorScheme="blue" size="md" />
                                                </Flex>
                                            )}
                                            {showRestockItemsOnlyToggle && (
                                                <Flex align="center" justify="space-between" cursor="pointer" onClick={() => setDraft(prev => ({
                                                    ...prev,
                                                    restockItemsOnly: !(prev.restockItemsOnly ?? false),
                                                }))}>
                                                    <HStack spacing={3}>
                                                        <Icon as={TbAlertTriangle as React.ElementType} boxSize={5} color="gray.500" />
                                                        <Box>
                                                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                                Restock Items Only
                                                            </Text>
                                                            <Text fontSize="xs" color="gray.500">
                                                                Scope In-Stock to restock-alert items and show Target / Reorder lines
                                                            </Text>
                                                        </Box>
                                                    </HStack>
                                                    <Switch isChecked={draft.restockItemsOnly ?? false} pointerEvents="none" colorScheme="blue" size="md" />
                                                </Flex>
                                            )}
                                            {showMarketplaceToggle && (
                                                <Flex align="center" justify="space-between" cursor="pointer" onClick={() => setDraft(prev => ({
                                                    ...prev,
                                                    showMarketplaceBreakdown: !(prev.showMarketplaceBreakdown ?? false),
                                                }))}>
                                                    <HStack spacing={3}>
                                                        <Icon as={TbBuildingStore as React.ElementType} boxSize={5} color="gray.500" />
                                                        <Box>
                                                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                                Show marketplace breakdown
                                                            </Text>
                                                            <Text fontSize="xs" color="gray.500">
                                                                {draft.widgetType === 'table'
                                                                    ? 'Expand each row into per-marketplace rows'
                                                                    : `Split bars by marketplace (${isStackedMarketplaceMetric(draft.metric) ? 'stacked' : 'clustered'})`}
                                                            </Text>
                                                        </Box>
                                                    </HStack>
                                                    <Switch isChecked={draft.showMarketplaceBreakdown ?? false} pointerEvents="none" colorScheme="blue" size="md" />
                                                </Flex>
                                            )}
                                            {draft.dataSource === 'rankings' && draft.widgetType !== 'pieChart' && (
                                                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                                                    <Box pr={4}>
                                                        <FormLabel fontSize="sm" fontWeight="medium" color="gray.600" mb={0}>
                                                            Show all rows
                                                        </FormLabel>
                                                        <Text fontSize="xs" color="gray.500" mt={1}>
                                                            Off shows the top 10 (20 for full-width). On shows all ranked entities regardless of widget size.
                                                        </Text>
                                                    </Box>
                                                    <Switch
                                                        size="sm"
                                                        isChecked={draft.showAllRows ?? false}
                                                        onChange={e => update('showAllRows', e.target.checked)}
                                                        colorScheme="blue"
                                                    />
                                                </FormControl>
                                            )}
                                            {showSortControls && (
                                                <FormControl>
                                                    <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
                                                        Default sort order
                                                    </FormLabel>
                                                    <PopoverSelect
                                                        value={draft.sort || 'desc'}
                                                        options={[
                                                            { value: 'desc', label: 'Descending' },
                                                            { value: 'asc', label: 'Ascending' },
                                                        ]}
                                                        onChange={selectedValue => update('sort', selectedValue as WidgetConfig['sort'])}
                                                    />
                                                </FormControl>
                                            )}
                                            {showLocationLimitControls && (
                                                <>
                                                    <FormControl display="flex" alignItems="center" justifyContent="space-between">
                                                        <Box pr={4}>
                                                            <FormLabel fontSize="sm" fontWeight="medium" color="gray.600" mb={0}>
                                                                Show all locations
                                                            </FormLabel>
                                                            <Text fontSize="xs" color="gray.500" mt={1}>
                                                                Off shows the top locations by total period value. On shows every location.
                                                            </Text>
                                                        </Box>
                                                        <Switch
                                                            size="sm"
                                                            isChecked={draft.showAllRows ?? false}
                                                            onChange={e => update('showAllRows', e.target.checked)}
                                                            colorScheme="blue"
                                                        />
                                                    </FormControl>
                                                    {!(draft.showAllRows ?? false) && (
                                                        <FormControl>
                                                            <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
                                                                Location limit
                                                            </FormLabel>
                                                            <Input
                                                                type="number"
                                                                min={1}
                                                                max={50}
                                                                value={draft.cutoff ?? INVOICING_DEFAULT_LOCATION_LINE_CUTOFF}
                                                                onChange={e => {
                                                                    const next = parseInt(e.target.value, 10);
                                                                    update('cutoff', Number.isFinite(next) && next > 0 ? next : INVOICING_DEFAULT_LOCATION_LINE_CUTOFF);
                                                                }}
                                                            />
                                                        </FormControl>
                                                    )}
                                                </>
                                            )}
                                            {!hasAnyConfig && (
                                                <Text fontSize="sm" color="gray.500">
                                                    No additional options for this widget.
                                                </Text>
                                            )}
                                        </VStack>
                                    </AccordionPanel>
                                </AccordionItem>
                            </Box>
                        </Accordion>
                    </VStack>
        </AppDrawer>
    );
};

function SimpleGrid({ columns, spacing, children }: { columns: number; spacing: number; children: React.ReactNode; }) {
    return (
        <Box display="grid" gridTemplateColumns={`repeat(${columns}, 1fr)`} gap={spacing}>
            {children}
        </Box>
    );
}

export default WidgetEditDrawer;
